<script lang="ts" setup>
    import { computed, reactive, watch } from 'vue'
    import BzDialog from '~/components/bz/Dialog.vue'
    import { useFlowsStore } from '~~/stores/flows'
    import { useContextStore } from '~~/stores/context'

    const flowsStore = useFlowsStore()
    const contextStore = useContextStore()

    const inputs = computed(() => flowsStore.pendingProfilePrompt?.inputs ?? [])

    const show = computed({
        get: () => flowsStore.pendingProfilePrompt !== null,
        set: (open: boolean) => {
            if (!open) {
                flowsStore.cancelTriggerProfiles()
            }
        },
    })

    const selections = reactive<Record<string, string>>({})
    const optionsByInput = reactive<Record<string, { label: string, value: string }[]>>({})

    // Default precedence per the spec: template-assigned value ?? a compatible
    // profile from the user's context ?? empty.
    function defaultFor(input: { provider: string, defaultValue: string }): string {
        if (input.defaultValue) {
            return input.defaultValue
        }
        const userProfileId = contextStore.getContext(input.provider)?.profileId
        return userProfileId != null ? String(userProfileId) : ''
    }

    watch(
        inputs,
        async (list) => {
            for (const key of Object.keys(selections)) {
                delete selections[key]
            }
            for (const key of Object.keys(optionsByInput)) {
                delete optionsByInput[key]
            }
            for (const input of list) {
                selections[input.inputName] = defaultFor(input)
                const profiles = await flowsStore.fetchAvailableProfiles(input.provider)
                optionsByInput[input.inputName] = profiles.map((p) => ({
                    label: p.label,
                    value: String(p.id),
                }))
            }
        },
        { immediate: true },
    )

    const canTrigger = computed(() => inputs.value.every((i) => Boolean(selections[i.inputName])))

    async function confirm(): Promise<void> {
        if (!canTrigger.value) {
            return
        }
        await flowsStore.submitTriggerProfiles({ ...selections })
    }
</script>

<template>
    <bz-dialog
        v-model="show"
        title="Choose profiles"
        confirm-label="Trigger"
        :confirm-disabled="!canTrigger"
        @confirm="confirm"
    >
        <q-select
            v-for="input in inputs"
            :key="input.inputName"
            v-model="selections[input.inputName]"
            :options="optionsByInput[input.inputName]"
            :label="input.inputName"
            :provider="input.provider"
            emit-value
            map-options
            outlined
        />
    </bz-dialog>
</template>
